// src/pages/SeniorAnalystPitches.tsx
// Dedicated Pitch Management Page for Senior Analysts

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  FileText, 
  Briefcase,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Search,
  Eye,
  UserCheck,
  Trash2,
  UserPlus,
  ArrowLeft
} from 'lucide-react';

// Helper function for initials
const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Stat Card Component
function StatCard({ title, value, description, icon: Icon, color }: any) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          </div>
          <div className={`${color} p-2 rounded-lg text-white`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SeniorAnalystPitches() {
  const { user, allUsers } = useAuth();
  const { pitches, adminDeletePitch, assignPitch } = useData();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [pitchToDelete, setPitchToDelete] = useState<string | null>(null);
  const [pitchToAssign, setPitchToAssign] = useState<string | null>(null);
  const [selectedAnalyst, setSelectedAnalyst] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStage, setFilterStage] = useState<string>('all');

  // Get all pitches (senior analysts can see all)
  const allPitches = pitches.filter(p => !p.deletedAt);
  
  // ONLY Business Analysts - NOT senior analysts
  const businessAnalysts = allUsers.filter(u => 
    u.role === 'analyst' && u.analyst_type === 'business'
  );
  
  const hasBusinessAnalysts = businessAnalysts.length > 0;

  const getStageColor = (stage: string) => {
    const colors: Record<string, string> = {
      idea: 'bg-gray-500',
      analysis: 'bg-blue-500',
      awaiting_assignment: 'bg-yellow-500',
      validation: 'bg-purple-500',
      contract: 'bg-indigo-500',
      bootcamp: 'bg-orange-500',
      diligence: 'bg-red-500',
      funding: 'bg-green-500',
      deleted: 'bg-gray-400',
    };
    return colors[stage] || 'bg-gray-500';
  };

  const getAssignedAnalystName = (analystId: string) => {
    const analyst = allUsers.find(u => u.id === analystId);
    return analyst?.name || 'Unknown';
  };

  const handleAdminDeletePitch = () => {
    if (pitchToDelete) {
      adminDeletePitch(pitchToDelete);
      setPitchToDelete(null);
      toast({
        title: "Pitch deleted",
        description: "The pitch has been removed from the system.",
      });
    }
  };

  const handleAssignClick = (pitchId: string) => {
    setPitchToAssign(pitchId);
    setSelectedAnalyst('');
  };

  const handleAssignPitch = () => {
    if (pitchToAssign && selectedAnalyst) {
      assignPitch(pitchToAssign, selectedAnalyst, user?.id);
      setPitchToAssign(null);
      setSelectedAnalyst('');
      toast({
        title: "Pitch assigned",
        description: "The pitch has been assigned to the Business Analyst.",
      });
    }
  };

  // Filter pitches
  const filteredPitches = allPitches.filter(pitch => {
    if (filterStage !== 'all' && pitch.stage !== filterStage) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        pitch.companyName?.toLowerCase().includes(query) ||
        pitch.tagline?.toLowerCase().includes(query) ||
        pitch.founderName?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const unassignedCount = allPitches.filter(p => !p.assignedAnalystId && p.stage !== 'funding' && p.stage !== 'deleted').length;

  // Handle back button - go to senior analyst dashboard with problem bank view
  const handleBack = () => {
    navigate('/senior-analyst?tab=problems');
  };

  return (
    <div className="w-full min-h-screen bg-background">
      <div className="w-full px-6 py-4">
        {/* Header with Back Button */}
        <div className="mb-4">
          <Button 
            variant="ghost" 
            size="sm"
            className="mb-2 -ml-2 text-muted-foreground h-8"
            onClick={handleBack}
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">Pitch Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Assign pitches to Business Analysts and track their progress through the funding pipeline.
          </p>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard 
            title="Total Pitches" 
            value={allPitches.length.toString()}
            description="Active in pipeline"
            icon={FileText}
            color="bg-blue-500"
          />
          <StatCard 
            title="Unassigned" 
            value={unassignedCount.toString()}
            description="Awaiting assignment"
            icon={AlertCircle}
            color={unassignedCount > 0 ? "bg-yellow-500" : "bg-green-500"}
          />
          <StatCard 
            title="In Validation" 
            value={allPitches.filter(p => p.stage === 'validation').length.toString()}
            description="Meeting stage"
            icon={UserCheck}
            color="bg-purple-500"
          />
          <StatCard 
            title="Funded" 
            value={allPitches.filter(p => p.stage === 'funding').length.toString()}
            description="Successfully funded"
            icon={TrendingUp}
            color="bg-green-500"
          />
        </div>

        {/* No Business Analysts Warning */}
        {!hasBusinessAnalysts && (
          <Alert className="mb-4 border-yellow-200 bg-yellow-50">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertTitle className="text-yellow-800">No Business Analysts Available</AlertTitle>
            <AlertDescription className="text-yellow-700">
              You need to create Business Analyst users before you can assign pitches.{' '}
              <Button 
                variant="link" 
                className="text-yellow-800 p-0 h-auto font-semibold"
                onClick={() => navigate('/admin/users')}
              >
                Create Business Analyst →
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Pitches Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">All Pitches</CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  {filteredPitches.length} pitch{filteredPitches.length !== 1 ? 'es' : ''} shown
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search pitches..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="pl-8 h-8 text-sm w-48"
                  />
                </div>
                <Select value={filterStage} onValueChange={setFilterStage}>
                  <SelectTrigger className="h-8 text-sm w-36">
                    <SelectValue placeholder="All stages" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stages</SelectItem>
                    <SelectItem value="idea">Idea</SelectItem>
                    <SelectItem value="analysis">Analysis</SelectItem>
                    <SelectItem value="awaiting_assignment">Awaiting Assignment</SelectItem>
                    <SelectItem value="validation">Validation</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="bootcamp">Bootcamp</SelectItem>
                    <SelectItem value="diligence">Diligence</SelectItem>
                    <SelectItem value="funding">Funding</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Company</TableHead>
                  <TableHead className="text-xs">Founder</TableHead>
                  <TableHead className="text-xs">Stage</TableHead>
                  <TableHead className="text-xs">Score</TableHead>
                  <TableHead className="text-xs">Assigned To</TableHead>
                  <TableHead className="text-xs">Created</TableHead>
                  <TableHead className="text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPitches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No pitches found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPitches.map(pitch => {
                    const founder = allUsers.find(u => u.id === pitch.founderId);
                    return (
                      <TableRow key={pitch.id}>
                        <TableCell className="font-medium whitespace-nowrap text-sm">
                          {pitch.companyName || 'Untitled Pitch'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6 shrink-0">
                              <AvatarFallback className="text-xs">
                                {founder ? getInitials(founder.name) : '?'}
                              </AvatarFallback>
                            </Avatar>
                            <span className="truncate max-w-[120px] text-sm">{founder?.name || 'Unknown'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStageColor(pitch.stage)} text-white whitespace-nowrap text-xs px-2 py-0.5`}>
                            {pitch.stage?.replace('_', ' ') || 'idea'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {pitch.analysisScores ? (
                            <span className={`font-medium whitespace-nowrap text-sm ${
                              pitch.analysisScores.overall >= 70 ? 'text-green-600' :
                              pitch.analysisScores.overall >= 50 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {pitch.analysisScores.overall}/100
                            </span>
                          ) : (
                            <span className="text-muted-foreground whitespace-nowrap text-sm">Pending</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {pitch.assignedAnalystId ? (
                            <Badge variant="outline" className="bg-blue-50 whitespace-nowrap text-xs">
                              {getAssignedAnalystName(pitch.assignedAnalystId)}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 whitespace-nowrap text-xs">
                              Unassigned
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap text-sm">
                          {new Date(pitch.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {!pitch.assignedAnalystId && pitch.stage !== 'funding' ? (
                              <Button 
                                variant="default" 
                                size="sm"
                                onClick={() => handleAssignClick(pitch.id)}
                                className="bg-green-600 hover:bg-green-700 whitespace-nowrap h-8 text-xs"
                                disabled={!hasBusinessAnalysts}
                                title={!hasBusinessAnalysts ? "No Business Analysts available to assign" : ""}
                              >
                                <UserCheck className="h-3 w-3 mr-1" />
                                Assign
                              </Button>
                            ) : (
                              <Button variant="outline" size="sm" className="h-8 text-xs" asChild>
                                <Link to={`/pitch/${pitch.id}`}>
                                  <Eye className="h-3 w-3 mr-1" />
                                  View
                                </Link>
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setPitchToDelete(pitch.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0 h-8 w-8"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!pitchToDelete} onOpenChange={() => setPitchToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this pitch?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. The pitch will be permanently deleted
                and the founder will be notified.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleAdminDeletePitch}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Assign Pitch Dialog - ONLY shows Business Analysts */}
        <Dialog open={!!pitchToAssign} onOpenChange={() => setPitchToAssign(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Pitch to Business Analyst</DialogTitle>
              <DialogDescription>
                Select a Business Analyst to evaluate this pitch.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select Business Analyst</Label>
                <Select value={selectedAnalyst} onValueChange={setSelectedAnalyst}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a Business Analyst" />
                  </SelectTrigger>
                  <SelectContent>
                    {!hasBusinessAnalysts ? (
                      <div className="p-4 text-center">
                        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm font-medium">No Business Analysts Available</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Please create a Business Analyst user in Admin → Users first.
                        </p>
                      </div>
                    ) : (
                      businessAnalysts.map(analyst => (
                        <SelectItem key={analyst.id} value={analyst.id}>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6 shrink-0">
                              <AvatarFallback className="text-xs">
                                {getInitials(analyst.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="text-sm font-medium">{analyst.name}</span>
                              <span className="text-xs text-muted-foreground">{analyst.email}</span>
                            </div>
                            <Badge variant="outline" className="ml-2 text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                              Business Analyst
                            </Badge>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                
                {!hasBusinessAnalysts && (
                  <div className="mt-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => navigate('/admin/users')}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Create Business Analyst
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPitchToAssign(null)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAssignPitch} 
                disabled={!selectedAnalyst || !hasBusinessAnalysts}
              >
                Assign Pitch
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}